import { FormProps, useForm } from "antd/es/form/Form";
import { createInstitution, IInstitution, updateInstitution } from "../../redux/reducer/institution";
import { Button, Checkbox, Col, Form, Input, Modal, Row } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router";
import { AxiosResponse } from "axios";

interface InstitutionsModalParams {
    open: boolean;
    onClose: () => void;
    institutionId?: string;
}

const InstitutionsModal = ({
    open,
    onClose,
    institutionId,
}: InstitutionsModalParams) => {
    const navigate = useNavigate();
    const { accounts } = useMsal();
    const dispatch = useDispatch<AppDispatch>();
    const institutions = useSelector<RootState, IInstitution[]>(({ institution }) => institution.institutions);
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => (l['institution/CREATE_INSTITUTION'] || l['institution/PATCH_INSTITUTION']));
    const [form] = useForm<Partial<IInstitution>>();
    const [domainWorks, setDomainWorks] = useState<boolean>(Boolean(institutionId));

    useEffect(() => { setDomainWorks(institutionId ? open : false); }, [open])
    useEffect(() => {
        if (!open) form.resetFields();
        else if (institutionId) {
            const institution = institutions.find(({ id }) => id === institutionId);
            if (institution) {
                form.setFieldsValue(institution);
            }
        }
    }, [open, institutionId]);

    const onFinish: FormProps<Partial<IInstitution>>['onFinish'] = (values) => {
        if (institutionId) {
            dispatch(updateInstitution({
                input: {
                    institutionId: institutionId,
                    id: institutions.find(({ id }) => id === institutionId)?.id,
                    ...values,
                },
                message: { success: 'Institution updated!' },
                headers: {
                    Authorization: `Bearer ${accounts[0].idToken}`,
                },
                onSuccess: onClose,
            }))
        } else {
            dispatch(createInstitution({
                input: {
                    ...values,
                },
                message: { success: 'Institution created!' },
                headers: {
                    Authorization: `Bearer ${accounts[0].idToken}`,
                },
                onSuccess: (res: AxiosResponse<IInstitution>) => {
                    navigate(`/institution/${res.data.id}`);
                    onClose();
                },
            }));
        }
    };

    return (
        <Modal
            onCancel={onClose}
            open={open}
            footer={null}
            title={`${institutionId ? 'Edit' : 'Create'} Institution`}
        >
            <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
                className="mt-8"
            >
                <Row gutter={8}>
                    <Col span={24}>
                        <Form.Item
                            name="institutionId"
                            label="Entra Tenant ID"
                            rules={[{
                                required: true,
                            }, {
                                pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
                                message: 'Entra Tenant ID must be a GUID'
                            }]}
                        >
                            <Input disabled={Boolean(institutionId)} placeholder="Entra Tenant ID" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="name"
                            label="Institution Name"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="Institution Name" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="host"
                            label="Host"
                            rules={[{ required: true }]}
                        >
                            <Input
                                placeholder="Host"
                                onChange={() => { setDomainWorks(false); }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Checkbox
                            checked={domainWorks}
                            onChange={(e) => { setDomainWorks(e.target.checked); }}
                        >
                            I have ensured that this link is set up
                        </Checkbox>
                    </Col>
                </Row>
                <Row justify='end' gutter={8}>
                    <Col>
                        <Button onClick={onClose}>
                            Cancel
                        </Button>
                    </Col>
                    <Col>
                        <Button
                            disabled={!domainWorks}
                            htmlType="submit"
                            type="primary"
                            loading={loading}
                        >
                            Submit
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default InstitutionsModal;
